import {
  Badge,
  Button,
  Collapse,
  Group,
  NumberInput,
  Paper,
  Stack,
  Textarea,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@auth/data-access';

import {
  OsLotReviewLink,
  OsLotReviewListItem,
  OsLotReviewSubmitPayload,
} from '../../lib/types/os-lot-reviews';

import {
  getLinkReviewMetrics,
  reviewStatusColor,
} from './os-lot-review-checks';
import {
  OsLotReviewIssueList,
  OsLotReviewMetricPanel,
} from './os-lot-review-metric';
import { OsLotReviewCorrectionSummary } from './os-lot-review-correction-summary';
import { OsLotReviewTelepacImport } from './os-lot-review-telepac';
import classes from './os-lot-reviews.module.css';

interface OsLotReviewLinkCardProps {
  link: OsLotReviewLink;
  lot: Pick<
    OsLotReviewListItem,
    'species' | 'theoretical_area' | 'links_count'
  >;
  isSubmitting: boolean;
  onSubmit: (payload: OsLotReviewSubmitPayload) => void;
}

function formatPratiqueIds(
  pk: number,
  oleozeId: number | null | undefined,
): string {
  return `${pk} / ${oleozeId ?? '—'}`;
}

type LinkMetaItem = {
  key: string;
  label: string;
  value: string | number;
  title?: string;
};

function toFiniteNumber(value: number | string) {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function OsLotReviewLinkCard({
  link,
  lot,
  isSubmitting,
  onSubmit,
}: OsLotReviewLinkCardProps) {
  const { t } = useTranslation();
  const { isAdmin, isIndustrial } = usePermissions();
  const showOrganizationFields = isAdmin || isIndustrial;
  const showTelepacFiles = !isIndustrial;
  const [correcting, setCorrecting] = useState(false);
  const [comment, setComment] = useState('');
  const [volume, setVolume] = useState<number | string>('');
  const [yieldValue, setYieldValue] = useState<number | string>('');
  const [files, setFiles] = useState<File[]>([]);

  const latest = link.latest_review;
  const metrics = getLinkReviewMetrics(link, lot);
  const status = latest?.status ?? 'pending';

  useEffect(() => {
    if (latest?.status === 'correction_proposed') {
      setCorrecting(false);
    }
  }, [latest?.id, latest?.status]);
  const cardClass = [
    classes.linkCard,
    latest?.status === 'attested' ? classes.linkCardAttested : '',
    latest?.status === 'correction_proposed' ? classes.linkCardCorrection : '',
  ]
    .filter(Boolean)
    .join(' ');

  const openCorrection = () => {
    setVolume(link.volume_reel ?? '');
    setYieldValue(link.rdt_reel ?? '');
    setFiles([]);
    setCorrecting(true);
  };

  const computedArea = (() => {
    const volumeValue = toFiniteNumber(volume);
    const yieldNumber = toFiniteNumber(yieldValue);
    if (volumeValue == null || yieldNumber == null || yieldNumber === 0) {
      return '';
    }
    return Math.round((volumeValue / yieldNumber) * 100) / 100;
  })();

  const proposedPayload = (): Omit<OsLotReviewSubmitPayload, 'status'> => ({
    comment: comment.trim() || undefined,
    proposed_volume_reel: volume === '' ? null : Number(volume),
    proposed_rdt_reel: yieldValue === '' ? null : Number(yieldValue),
    proposed_telepacs: files,
  });

  const metaItems: LinkMetaItem[] = [];

  const pratiqueIds = formatPratiqueIds(link.pratique_pk, link.oleoze_id);
  const pratiqueIdsHint = t('common.pratique-ids-hint', {
    pk: link.pratique_pk,
    oleoze: link.oleoze_id ?? '—',
  });

  if (link.product_campaign) {
    metaItems.push({
      key: 'pratique',
      label: t('osLotReviews.link.pratique'),
      title: pratiqueIdsHint,
      value: pratiqueIds,
    });
  }
  if (showOrganizationFields && link.organization_name) {
    metaItems.push({
      key: 'organization',
      label: t('osLotReviews.link.organization'),
      value: link.organization_name,
    });
  }
  if (showOrganizationFields && link.owner_name) {
    metaItems.push({
      key: 'owner',
      label: t('osLotReviews.link.owner'),
      value: link.owner_name,
    });
  }
  if (link.code_adherent) {
    metaItems.push({
      key: 'member_code',
      label: t('osLotReviews.link.member_code'),
      value: link.code_adherent,
    });
  }
  if (link.farmer_reference) {
    metaItems.push({
      key: 'farmer_ref',
      label: t('osLotReviews.link.farmer_ref'),
      value: link.farmer_reference,
    });
  }

  return (
    <Paper className={cardClass} p="md" withBorder>
      <Stack gap="md">
        <div className={classes.linkCardHead}>
          <h3 className={classes.linkCardTitle}>
            {link.product_campaign ||
              t('osLotReviews.link.title', { id: pratiqueIds })}
          </h3>
          {metaItems.length > 0 ? (
            <div
              aria-hidden="true"
              className={classes.linkCardSep}
            />
          ) : null}
          <div className={classes.linkCardMeta}>
            {metaItems.map((item) => (
              <span className={classes.linkMetaPill} key={item.key}>
                <span className={classes.linkMetaLabel}>{item.label}</span>
                <span className={classes.linkMetaValue} title={item.title}>
                  {item.value}
                </span>
              </span>
            ))}
          </div>
          <Badge
            className={classes.linkStatusBadge}
            color={reviewStatusColor(status)}
            variant="light"
          >
            {t(`osLotReviews.review_status.${status}`)}
          </Badge>
        </div>

        {lot.links_count > 1 ? (
          <OsLotReviewIssueList metrics={metrics} />
        ) : null}

        <div className={classes.linkMetrics}>
          <OsLotReviewMetricPanel
            label={t('osLotReviews.link.volume')}
            metric={metrics.tonnage}
            referenceLabel={t('osLotReviews.metrics.oleoze')}
          />
          <OsLotReviewMetricPanel
            label={t('osLotReviews.link.yield')}
            metric={metrics.yield}
            referenceLabel={t('osLotReviews.metrics.oleoze')}
          />
          <OsLotReviewMetricPanel
            label={t('osLotReviews.link.area')}
            metric={metrics.area}
            referenceLabel={t('osLotReviews.metrics.theoretical_total')}
          />
        </div>

        {latest?.status === 'attested' && latest.comment?.trim() ? (
          <p className={classes.commentBox}>{latest.comment}</p>
        ) : null}
        {latest ? (
          <OsLotReviewCorrectionSummary
            review={latest}
            showTelepacFiles={showTelepacFiles}
          />
        ) : null}

        <Collapse in={correcting}>
          <Stack gap="sm" pt="xs">
            <Textarea
              label={t('osLotReviews.form.comment')}
              minRows={2}
              onChange={(e) => setComment(e.currentTarget.value)}
              value={comment}
            />
            <Group align="flex-end" grow>
              <NumberInput
                decimalScale={2}
                label={t('osLotReviews.form.proposed_volume')}
                onChange={setVolume}
                value={volume}
              />
              <NumberInput
                decimalScale={2}
                label={t('osLotReviews.form.proposed_yield')}
                onChange={setYieldValue}
                value={yieldValue}
              />
              <NumberInput
                decimalScale={2}
                disabled
                label={t('osLotReviews.form.calculated_area')}
                value={computedArea}
              />
            </Group>
            {showTelepacFiles ? (
              <OsLotReviewTelepacImport files={files} onChange={setFiles} />
            ) : null}
          </Stack>
        </Collapse>
        <Group justify="flex-end">
          {correcting ? (
            <Button onClick={() => setCorrecting(false)} variant="subtle">
              {t('osLotReviews.form.cancel')}
            </Button>
          ) : null}
          <Button
            loading={isSubmitting}
            onClick={() =>
              onSubmit({
                status: 'attested',
                comment: comment.trim() || undefined,
              })
            }
            variant={correcting ? 'default' : 'filled'}
          >
            {t('osLotReviews.form.attest')}
          </Button>
          {correcting ? (
            <Button
              loading={isSubmitting}
              onClick={() =>
                onSubmit({
                  status: 'correction_proposed',
                  ...proposedPayload(),
                })
              }
            >
              {t('osLotReviews.form.correct')}
            </Button>
          ) : (
            <Button onClick={openCorrection} variant="default">
              {t('osLotReviews.form.correct')}
            </Button>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}
